import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { switchChain } from '@wagmi/core'
import { Rpc } from 'accounts'
import { CliAuth } from 'accounts/server'
import type { Hex } from 'ox'
import * as React from 'react'
import { Button, Frame, Input } from 'regen-ui'
import { tempo as tempoChain } from 'viem/chains'
import { useConnect, useConnection } from 'wagmi'
import * as z from 'zod/mini'
import Check from '~icons/lucide/check'
import Fingerprint from '~icons/lucide/fingerprint'

import { api } from '#/lib/api.js'
import * as Auth from '#/lib/auth.js'
import * as AuthorizedApps from '#/lib/authorized-apps.js'
import { wagmiConfig } from '#/lib/config.js'
import { useWebAuthnConnector } from '#/lib/connectors.js'
import { useFundingOptions } from '#/lib/funding-options.js'
import { useAnalytics } from '#/lib/telemetry/analytics.js'
import {
  AccessKeyPermissionsEditor,
  AccessKeyScopes,
  type AuthorizeAccessKey,
} from '#/routes/remote/-components/AccessKeyScopes.js'
import { UpdateAccessKeyFlow } from '#/routes/remote/-components/UpdateAccessKeyFlow.js'
import { AuthorizeCli } from '#/routes/remote/-frames/authorize/AuthorizeCli.js'
import { useDeposit } from '#/routes/remote/-hooks/useDeposit.js'
import * as CliAccessKeyUpdate from '#/routes/remote/-lib/CliAccessKeyUpdate.js'
import * as ShowDeposit from '#/routes/remote/-lib/ShowDeposit.js'

const completed = 'Device code already completed.'
const errorResponse = z.object({ error: z.string() })
type PendingDeposit = {
  /** Account that can optionally receive funds. */
  address: Hex.Hex
  /** Balance-gate result that decided to show this prompt. */
  balanceGate: ShowDeposit.BalanceGate
  /** Auth event that completed before the deposit prompt. */
  event: ShowDeposit.Event
  /** Requested deposit prompt to render. */
  showDeposit: ShowDeposit.Requested
}

/** Returns presentation details for a failed pending CLI request. */
export function getError(error: unknown) {
  const message = error instanceof Error ? error.message : 'Invalid device code.'
  if (message === completed)
    return {
      kind: 'completed' as const,
      message:
        'Return to your terminal. If setup is not complete, restart the connection to generate a new code.',
      subtitle: 'This one-time authorization request has already finished.',
      title: 'CLI request complete',
    }
  return {
    kind: 'invalid' as const,
    message,
    subtitle: "We couldn't load that device code.",
    title: 'Authorize CLI',
  }
}

/** Standalone CLI device code approval page. */
export const Route = createFileRoute('/remote/auth/cli')({
  validateSearch: z.object({ code: z.optional(z.string()) }),
  component: RouteComponent,
})

function RouteComponent() {
  const { capture } = useAnalytics()
  Route.useNavigate()
  // Standalone auth still requires a webAuthn session; read-only mode must
  // render the sign-in flow instead of the existing-user approval screen.
  const sessionAddress = Auth.useSessionAddress()
  const { address } = useConnection()
  const queryClient = useQueryClient()

  const { code } = Route.useSearch()
  const normalizedCode = React.useMemo(() => normalizeCode(code), [code])
  const code_authorized = React.useRef<string | undefined>(undefined)
  React.useEffect(() => {
    code_authorized.current = undefined
  }, [normalizedCode])
  React.useEffect(() => {
    capture('cli_auth_flow_started', { authed: !!sessionAddress })
  }, [capture, sessionAddress])

  const pending = useQuery({
    enabled: !!normalizedCode,
    queryKey: ['cli', 'pending', normalizedCode],
    queryFn: async () => {
      const response = await api.api.auth.cli.pending[':code'].$get({
        param: { code: normalizedCode },
      })
      if (!response.ok) {
        const body = errorResponse.safeParse(await response.json())
        throw new Error(body.success ? body.data.error : 'Failed to fetch pending CLI request.', {
          cause: response.statusText,
        })
      }
      return CliAuth.pendingResponse.parse(await response.json())
    },
    // A locally completed request becomes unreadable. Preserve it while funding
    // changes focus; other requests must keep revalidating until completion.
    refetchOnReconnect: () => code_authorized.current !== normalizedCode,
    refetchOnWindowFocus: () => code_authorized.current !== normalizedCode,
    retry: (failureCount, error) => error.message !== completed && failureCount < 3,
  })
  const error_pending = getError(pending.error)
  React.useEffect(() => {
    if (pending.isError && error_pending.kind === 'completed')
      capture('cli_auth_completed_code_viewed')
  }, [capture, pending.isError, error_pending.kind])

  const connector = useWebAuthnConnector()
  const fundingOptions = useFundingOptions()

  const authorizeAccessKey: AuthorizeAccessKey | undefined =
    pending.data && pending.data.action !== 'updateAccessKey'
      ? {
          address: pending.data.accessKeyAddress,
          chainId: pending.data.chainId,
          expiry: pending.data.expiry,
          keyType: pending.data.keyType,
          limits: pending.data.limits,
          publicKey: pending.data.pubKey,
        }
      : undefined
  const [draftAuthorizeAccessKey, setDraftAuthorizeAccessKey] = React.useState<AuthorizeAccessKey>()
  const [pendingDeposit, setPendingDeposit] = React.useState<PendingDeposit | undefined>()

  React.useEffect(() => {
    setDraftAuthorizeAccessKey(authorizeAccessKey)
    setPendingDeposit(undefined)
  }, [pending.data?.code])

  const currentAuthorizeAccessKey = draftAuthorizeAccessKey ?? authorizeAccessKey
  const [screen, setScreen] = React.useState<'edit-permissions' | 'review'>('review')
  const useFeePayerForCliAccessKey = fundingOptions.data?.coinflow === true

  const submitAuthorization = async (params: {
    accountAddress: string
    code: string
    keyAuthorization: unknown
  }) => {
    const request = CliAuth.authorizeRequest.parse({
      code: params.code,
      accountAddress: params.accountAddress,
      keyAuthorization: params.keyAuthorization,
    })
    const response = await fetch('/api/auth/cli', {
      body: JSON.stringify(z.encode(CliAuth.authorizeRequest as never, request as never)),
      headers: { 'content-type': 'application/json' },
      method: 'POST',
    })
    const body = parseErrorResponse(await response.text())
    if (!response.ok) throw new Error(body?.error ?? 'Failed to authorize CLI access.')
  }

  const showDepositCapability =
    pending.data?.action !== 'updateAccessKey' ? pending.data?.showDeposit : undefined
  const chainId_resolved = pending.data?.chainId
    ? Number(BigInt(pending.data.chainId))
    : tempoChain.id

  async function getPendingDeposit(options: { address: Hex.Hex; event: ShowDeposit.Event }) {
    const requestedShowDeposit = ShowDeposit.resolve(showDepositCapability, options.event)
    if (!requestedShowDeposit) return undefined
    const gate = await ShowDeposit.checkBalanceGate({
      address: options.address,
      chainId: chainId_resolved,
      queryClient,
      showDeposit: requestedShowDeposit,
    })
    if (gate.status === 'skip') return undefined
    return {
      address: options.address,
      balanceGate: gate,
      event: options.event,
      showDeposit: requestedShowDeposit,
    } satisfies PendingDeposit
  }

  const authorize = useMutation({
    mutationFn: async () => {
      capture('cli_auth_authorize_started')
      const current = pending.data
      if (!current) throw new Error('Missing pending request.')
      if (current.action === 'updateAccessKey')
        throw new Error('Expected an access key authorization request.')
      if (!currentAuthorizeAccessKey) throw new Error('Missing access key request.')

      const chainId = Number(BigInt(current.chainId))
      await switchChain(wagmiConfig, { chainId: chainId as never })

      const provider = await connector.getProvider()
      const params = Rpc.wallet_authorizeAccessKey.parameters.parse({
        ...currentAuthorizeAccessKey,
      })
      const encodedParams = z.encode(
        Rpc.wallet_authorizeAccessKey.parameters as never,
        params as never,
      )
      const result = (await provider.request({
        method: 'wallet_authorizeAccessKey',
        params: [
          useFeePayerForCliAccessKey
            ? { ...(encodedParams as object), feePayer: true }
            : encodedParams,
        ],
      } as never)) as Rpc.wallet_authorizeAccessKey.Encoded['returns']

      await AuthorizedApps.upsert({
        chainId,
        hostname: AuthorizedApps.cliHostname,
        key: { ...params, address: result.keyAuthorization.address ?? params.address },
      })

      await submitAuthorization({
        accountAddress: result.rootAddress,
        code: current.code,
        keyAuthorization: result.keyAuthorization,
      })
      code_authorized.current = normalizedCode
      setPendingDeposit(await getPendingDeposit({ address: result.rootAddress, event: 'login' }))
    },
    onSuccess: () => {
      capture('cli_auth_authorize_success')
    },
    onError: (error) => {
      capture('cli_auth_authorize_failed', { error: error.message })
    },
  })

  const connect = useConnect({
    mutation: {
      async onSuccess(data, variables) {
        const current = pending.data
        if (!current) return
        if (current.action === 'updateAccessKey') return
        const accounts = data.accounts
        const account = accounts[0]
        if (typeof account === 'string') return
        const keyAuth = account?.capabilities?.keyAuthorization
        if (!account || !keyAuth) return
        capture('cli_auth_authorize_started')
        try {
          if (authorizeAccessKey)
            await AuthorizedApps.upsert({
              chainId: Number(BigInt(current.chainId)),
              hostname: AuthorizedApps.cliHostname,
              key: {
                ...authorizeAccessKey,
                address: keyAuth.address ?? authorizeAccessKey.address,
              },
            })
          await submitAuthorization({
            accountAddress: account.address,
            code: current.code,
            keyAuthorization: keyAuth,
          })
          code_authorized.current = normalizedCode
          setPendingDeposit(
            await getPendingDeposit({
              address: account.address,
              event: variables.capabilities?.method === 'register' ? 'register' : 'login',
            }),
          )
          capture('cli_auth_authorize_success')
        } catch (error) {
          capture('cli_auth_authorize_failed', {
            error: error instanceof Error ? error.message : 'Unknown error',
          })
        }
      },
    },
  })

  const requestedShowDeposit = pendingDeposit?.showDeposit
  const requestedDeposit = !!requestedShowDeposit
  const showDepositParams =
    typeof requestedShowDeposit === 'object' ? requestedShowDeposit : undefined
  const depositAddress = pendingDeposit?.address ?? address ?? sessionAddress
  const requestedDepositVisible = requestedDeposit
  const deposit = useDeposit({
    address: depositAddress,
    amount: showDepositParams?.amount,
    chainId: chainId_resolved,
    orActions: requestedDepositVisible ? null : undefined,
    token: showDepositParams?.token,
  })
  // Existing users see a muted "Deposit funds" button instead of the inline picker.
  const [showDepositActions, setShowDepositActions] = React.useState(false)

  if (!normalizedCode)
    return (
      <Frame
        subtitle="Paste the device code shown in your terminal to continue."
        title="Authorize CLI"
      >
        <CodeForm />
      </Frame>
    )

  if (pending.isPending)
    return (
      <Frame subtitle="Loading the pending CLI request." title="Authorize CLI">
        <p className="text-copy-14 text-foreground-secondary">Loading…</p>
      </Frame>
    )

  if (pending.isError || !pending.data)
    return (
      <Frame subtitle={error_pending.subtitle} title={error_pending.title}>
        <p
          className="text-copy-14 text-negative data-[completed=true]:text-foreground-secondary"
          data-completed={error_pending.kind === 'completed'}
        >
          {error_pending.message}
        </p>
        <CodeForm />
      </Frame>
    )

  if (CliAccessKeyUpdate.isRequest(pending.data)) {
    const request = pending.data
    if (
      CliAccessKeyUpdate.canReview({
        account: address,
        requestAccount: pending.data.account,
        sessionAddress,
      })
    )
      return (
        <UpdateAccessKeyFlow
          connector={connector}
          key={request.code}
          onComplete={async ({ keyAuthorization }) => {
            const response = await fetch('/api/auth/cli', {
              body: JSON.stringify(
                CliAccessKeyUpdate.getCompletionRequest({ keyAuthorization, request }),
              ),
              headers: { 'content-type': 'application/json' },
              method: 'POST',
            })
            const body = parseErrorResponse(await response.text())
            if (!response.ok)
              throw new Error(body?.error ?? 'Failed to complete access key update.')
            code_authorized.current = normalizedCode
          }}
          onReject={() => window.close()}
          request={request}
        />
      )
    const selectingAccount = !!sessionAddress
    return (
      <Frame
        subtitle={
          selectingAccount
            ? 'Select the requested account to update this CLI access key.'
            : 'Sign in to update this CLI access key.'
        }
        title="Update spend limit"
        footer={
          <Button
            icon={<Fingerprint className="size-4" />}
            loading={connect.isPending}
            onClick={() =>
              connect.mutate({
                connector,
                withCapabilities: true,
                capabilities: { method: 'login', selectAccount: true },
              })
            }
            variant="primary"
          >
            {selectingAccount ? 'Select account' : 'Sign in with passkey'}
          </Button>
        }
      >
        <div className="flex flex-col gap-3 rounded-body bg-pane px-4 py-5 text-center">
          <p className="text-label-12 text-foreground-secondary">
            Confirm this code matches your terminal
          </p>
          <p className="font-mono text-heading-32 tracking-[0.3em]">
            {formatCode(pending.data.code)}
          </p>
        </div>
        {connect.error && <p className="text-label-13 text-negative">{connect.error.message}</p>}
      </Frame>
    )
  }

  if (screen === 'edit-permissions' && currentAuthorizeAccessKey && authorizeAccessKey)
    return (
      <AccessKeyPermissionsEditor
        authorizeAccessKey={currentAuthorizeAccessKey}
        expiry_original={authorizeAccessKey.expiry}
        onCancel={() => setScreen('review')}
        onSave={(next) => {
          setDraftAuthorizeAccessKey(next)
          setScreen('review')
        }}
      />
    )

  if (authorize.isSuccess || connect.isSuccess) {
    if (deposit.frame) return deposit.frame
    if (requestedDepositVisible)
      return (
        <Frame
          bodyClassName="gap-2"
          subtitle={ShowDeposit.getSubtitle({
            amount: showDepositParams?.amount,
            balanceGate: pendingDeposit.balanceGate,
            event: pendingDeposit.event,
            host: AuthorizedApps.cliHostname,
            token: showDepositParams?.token,
          })}
          title="Fund your account"
        >
          {deposit.actions}
        </Frame>
      )
    const expanded = showDepositActions
    return (
      <>
        <SuccessHeader
          subtitle="Setup is complete. You can return to the CLI."
          title="Return to your terminal"
        />
        <div className="flex flex-col gap-4 px-5 pb-4">
          <AccessKeyScopes authorizeAccessKey={currentAuthorizeAccessKey!} />
          {address &&
            (expanded ? (
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <h3 className="shrink-0 text-label-13 text-foreground-secondary">
                    Fund your account
                  </h3>
                  <div className="h-px flex-1 bg-(--border-color-border)" />
                </div>
                {deposit.actions}
              </div>
            ) : (
              <Button onClick={() => setShowDepositActions(true)} variant="secondary">
                Deposit funds
              </Button>
            ))}
        </div>
      </>
    )
  }

  const host = window.location.host

  if (sessionAddress)
    return (
      <>
        <AuthorizeCli
          code={formatCode(pending.data.code)}
          confirming={authorize.isPending}
          expiry_original={authorizeAccessKey?.expiry}
          host={host}
          authorizeAccessKey={currentAuthorizeAccessKey!}
          onApprove={() => authorize.mutate()}
          onAuthorizeAccessKeyChange={setDraftAuthorizeAccessKey}
          onReject={() => window.close()}
        />
        {authorize.error && (
          <div className="mt-auto px-5 pb-4 pt-0">
            <p className="text-label-13 text-negative">{authorize.error.message}</p>
          </div>
        )}
      </>
    )

  return (
    <Frame
      subtitle="Authorize this CLI to spend from your account."
      title="Authorize CLI"
      footerClassName="pt-0"
      footer={
        <>
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault()
              const label = String(new FormData(e.currentTarget).get('label') ?? '').trim()
              if (!label) return
              connect.mutate({
                connector,
                withCapabilities: true,
                capabilities: {
                  method: 'register',
                  name: label,
                  authorizeAccessKey: currentAuthorizeAccessKey,
                },
              })
            }}
          >
            <p className="text-copy-15 text-foreground-secondary">
              Create or sign in with your passkey to continue.
            </p>
            <Input name="label" placeholder="Email address or label…" required />
            {(authorize.error || connect.error) && (
              <p className="text-label-13 text-negative">
                {authorize.error?.message ?? connect.error?.message}
              </p>
            )}
            <Button
              loading={connect.isPending && connect.variables?.capabilities?.method === 'register'}
              type="submit"
              variant="primary"
            >
              Create account
            </Button>
            <div className="flex items-center gap-3">
              <div className="h-0 flex-1 border-t border-border" />
              <p className="text-label-12 text-foreground-secondary">or</p>
              <div className="h-0 flex-1 border-t border-border" />
            </div>
            <Button
              loading={connect.isPending && connect.variables?.capabilities?.method === 'login'}
              onClick={() =>
                connect.mutate({
                  connector,
                  withCapabilities: true,
                  capabilities: {
                    method: 'login',
                    authorizeAccessKey: currentAuthorizeAccessKey,
                    selectAccount: true,
                  },
                })
              }
              icon={<Fingerprint className="size-4" />}
              type="button"
              variant="secondary"
            >
              Sign in with passkey
            </Button>
          </form>
        </>
      }
    >
      <div className="flex flex-col gap-3 rounded-body bg-pane px-4 py-5 text-center">
        <p className="text-label-12 text-foreground-secondary">
          Confirm this code matches your terminal
        </p>
        <p className="font-mono text-heading-32 tracking-[0.3em]">
          {formatCode(pending.data.code)}
        </p>
      </div>
      <AccessKeyScopes
        authorizeAccessKey={currentAuthorizeAccessKey!}
        onEdit={() => setScreen('edit-permissions')}
      />
    </Frame>
  )
}

function CodeForm() {
  const navigate = Route.useNavigate()

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault()
        const form = new FormData(event.currentTarget)
        const value = String(form.get('code') ?? '')
        void navigate({ search: { code: normalizeCode(value) } })
      }}
    >
      <Input autoFocus label="Device code" name="code" placeholder="ABCD-EFGH…" />
      <Button type="submit" variant="primary">
        Load request
      </Button>
    </form>
  )
}

function SuccessHeader(props: { subtitle: string; title: string }) {
  return (
    <div className="flex flex-col gap-3 px-5 pt-4 pb-3">
      <div className="flex items-center gap-3">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-body bg-positive-subtle text-positive">
          <Check className="size-5" />
        </div>
        <h2 className="text-heading-20">{props.title}</h2>
      </div>
      <p className="text-copy-15 text-foreground-secondary">{props.subtitle}</p>
    </div>
  )
}

function normalizeCode(value: string | undefined) {
  if (!value) return ''
  return value.replace(/\s|-/g, '').toUpperCase()
}

function parseErrorResponse(text: string) {
  const value = text.trim()
  if (!value) return undefined

  try {
    const json = JSON.parse(value)
    const body = errorResponse.safeParse(json)
    return body.success ? body.data : undefined
  } catch {
    return undefined
  }
}

function formatCode(value: string) {
  const normalized = normalizeCode(value)
  if (normalized.length !== 8) return normalized
  return `${normalized.slice(0, 4)}-${normalized.slice(4)}`
}
